import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './News.module.css';
import API_BASE_URL from '../../config/api';

const News = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const lang = language === 'ar' ? 'ar' : 'en';

    const fetchNews = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/news?lang=${lang}`, { signal: controller.signal });
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.data || [];
        setNewsItems(items);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Failed to load news', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
    return () => controller.abort();
  }, [language]);

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className={styles.newsPage}>
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('newsPage.title')}</h1>
          <div className={styles.heroUnderline}></div>
          <p className={styles.heroSubtitle}>{t('newsPage.subtitle')}</p>
        </div>
      </section>

      <section className={styles.contentSection}>
        <div className={styles.container}>
          {loading ? (
            <div className={styles.loadingWrapper}>
              <div className={styles.spinner}></div>
            </div>
          ) : newsItems.length === 0 ? (
            <p className={styles.emptyMessage}>{t('newsPage.noNews')}</p>
          ) : (
            <div className={styles.newsGrid}>
              {newsItems.map((news) => (
                <article key={news.slug || news.id} className={styles.newsCard}>
                  <div className={styles.newsImageWrapper}>
                    {news.image_url ? (
                      <img src={news.image_url} alt={news.title} className={styles.newsImage} />
                    ) : (
                      <div className={styles.newsImagePlaceholder} />
                    )}
                    <div className={styles.newsOverlay}></div>
                  </div>
                  <div className={styles.newsContent}>
                    <div className={styles.newsDate}>{formatDate(news.date)}</div>
                    <h3 className={styles.newsTitle}>{news.title}</h3>
                    <p className={styles.newsSummary}>{news.summary}</p>
                    <Link to={`/news/${news.slug}`} className={styles.readMoreBtn}>
                      <span>{t('newsPage.readMore')}</span>
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default News;
